import { useEffect, useRef, useState } from 'react';
import { Box, Step } from '@mui/material';
import ConfirmationModal from './ConfirmationModal';
import StepLabel from './StepLabel/StepLabel';
import { ScrollableContainer, StyledStepper } from './ValuationStatusTracker.styles';
import { StepStatus, ValuationStatusTrackerProps } from './ValuationStatusTracker.types';

const ValuationStatusTracker = ({
  statusOptions,
  currentStatusId,
  onStatusChange,
  isLoading = false,
  disabled = false,
  isExpanded = false,
  fixedHeight,
}: ValuationStatusTrackerProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStepIndex, setPendingStepIndex] = useState<number | null>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync activeStep with currentStatusId prop
  useEffect(() => {
    if (currentStatusId) {
      const stepIndex = statusOptions.findIndex(option => option.id === currentStatusId);
      if (stepIndex !== -1 && stepIndex !== activeStep) {
        setActiveStep(stepIndex);
      }
    }
  }, [activeStep, currentStatusId, statusOptions]);

  // Scroll to center the active step
  useEffect(() => {
    if (stepRefs.current[activeStep] && containerRef.current) {
      const activeElement = stepRefs.current[activeStep];
      const container = containerRef.current;

      if (activeElement) {
        const containerHeight = container.clientHeight;
        const elementTop = activeElement.offsetTop;
        const elementHeight = activeElement.offsetHeight;
        const scrollPosition = elementTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth',
        });
      }
    }
  }, [activeStep]);

  const getStep = (index: number): StepStatus => {
    const isLastStep = index === statusOptions.length - 1;
    const isCurrentStep = activeStep === index;

    // If this is the last step and it's the current step, show as completed
    if (isLastStep && isCurrentStep) {
      return StepStatus.COMPLETED;
    }

    // Current active step (but not the last one)
    if (isCurrentStep) {
      return StepStatus.ACTIVE;
    }

    // Completed steps (before current step)
    if (activeStep > index) {
      return StepStatus.COMPLETED;
    }

    // All other steps are pending
    return StepStatus.PENDING;
  };

  const handleSelectStep = (index: number) => {
    if (disabled || isLoading) {
      return;
    }

    // Don't show confirmation if clicking the same step
    if (index === activeStep) {
      return;
    }

    setPendingStepIndex(index);
    setConfirmDialogOpen(true);
  };

  const handleConfirmStatusChange = () => {
    if (pendingStepIndex !== null) {
      const selectedOption = statusOptions[pendingStepIndex];
      if (selectedOption && onStatusChange) {
        onStatusChange(selectedOption.id);
      }
    }
    setConfirmDialogOpen(false);
    setPendingStepIndex(null);
  };

  const handleCancelStatusChange = () => {
    setConfirmDialogOpen(false);
    setPendingStepIndex(null);
  };

  const getPendingStatusLabel = () => {
    if (pendingStepIndex !== null) {
      return statusOptions[pendingStepIndex]?.label || '';
    }
    return '';
  };

  return (
    <>
      <ScrollableContainer ref={containerRef} isExpanded={isExpanded} fixedHeight={fixedHeight}>
        <StyledStepper activeStep={activeStep} orientation="vertical">
          {statusOptions.map((option, index) => (
            <Box
              key={option.id}
              ref={el => {
                if (el) {
                  stepRefs.current[index] = el;
                }
              }}>
              <Step>
                <StepLabel
                  label={option.label}
                  step={getStep(index)}
                  onClick={() => handleSelectStep(index)}
                  isLoading={isLoading}
                  disabled={disabled}
                />
              </Step>
            </Box>
          ))}
        </StyledStepper>
      </ScrollableContainer>

      <ConfirmationModal
        open={confirmDialogOpen}
        title="Confirm Status Change"
        message="Are you sure you want to change the valuation status to"
        highlightedText={getPendingStatusLabel()}
        onConfirm={handleConfirmStatusChange}
        onCancel={handleCancelStatusChange}
      />
    </>
  );
};

export default ValuationStatusTracker;
